import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import axios, { AxiosError } from "axios";
import { memo, useState } from "react";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import { TariffeData } from "../tariffeDefinizioni/TariffeData";
import { Loading } from "../ui/Loading";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Cards, Tariffe_Costo, Tipologia_Uso } from "../prismaTypes";
import { EuroFormatter } from "../utils/EuroFormatter";
import { useTheme } from "@mui/material/styles";
import InfoIcon from "@mui/icons-material/Info";
import { oraPrenotaStore } from "../oraPrenotaStore";

interface Tipologie {
  id: number;
  ClubId: number;
  denominazione: string;
  esenzionePagamentoOre: string;
  sforamentoFidoConsentito: string;
  condizioni: string;
}

const TariffaRow = memo(function TariffaRow(props: { t: TariffeData }) {
  const { t } = props;

  const [open, setOpen] = useState(false);

  const role = oraPrenotaStore((state) => state.role);
  const isUser = role === "USER";

  const { isLoading: isLoading6, data: card } = useQuery<{ cards: Cards[] }, AxiosError>(
    ["cards/findMyCard"],
    () => axios.get(API_URL + "cards/findMyCard").then((res) => res.data),
    {
      enabled: isUser,
    }
  );

  const { isLoading, data } = useQuery<{ tariffe: Tariffe_Costo[] }, AxiosError>(
    ["tariffe-costo", t.id],
    () => axios.get(API_URL + "tariffe-costo/findAll/" + t.id).then((res) => res.data),
    {
      enabled: open,
    }
  );

  const { isLoading: isLoading2, data: tip } = useQuery<
    unknown,
    AxiosError,
    { tipologie: Tipologie[] }
  >(
    ["tipologie-associazioni/findAll"],
    () => axios.get(API_URL + "tipologie-associazioni/findAll").then((res) => res.data),
    {
      enabled: open,
    }
  );

  const { isLoading: isLoadingUso, data: uso } = useQuery<
    unknown,
    AxiosError,
    { tipologieUso: Tipologia_Uso[] }
  >(
    ["tipologie-uso/findAll"],
    () => axios.get(API_URL + "tipologie-uso/findAll").then((res) => res.data),
    {
      enabled: open,
    }
  );

  if (isLoading || isLoading2 || isLoadingUso || isLoading6) return <Loading />;

  const myTips =
    tip?.tipologie.filter((t) => !isUser || t.id === card?.cards[0].Tipologia_AssociazioniId) ?? [];

  const uso2 =
    uso?.tipologieUso.filter((u) =>
      myTips.some(
        (tipo) =>
          data?.tariffe.find(
            (c) => c.tipologia_AssociazioniId === tipo.id && c.tipologia_UsoId === u.id
          )?.costoPerSlot
      )
    ) ?? [];

  return (
    <>
      <TableRow sx={{ "& > *": { border: "unset" } }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ backgroundColor: t.colore.esadecimale }}></TableCell>
        <TableCell>{t.nome}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell></TableCell>
                    {uso2.map((u) => (
                      <TableCell key={u.id}>{u.descrizioneUso}</TableCell>
                    ))}
                  </TableRow>

                  {myTips.map((tipo) => (
                    <TableRow key={tipo.id}>
                      <TableCell>{tipo.denominazione}</TableCell>
                      {uso2.map((u) => {
                        const costo = data?.tariffe.find(
                          (c) =>
                            c.tipologia_AssociazioniId === tipo.id && c.tipologia_UsoId === u.id
                        )?.costoPerSlot;

                        return (
                          <TableCell key={u.id}>
                            {costo ? EuroFormatter.format(costo) : "-"}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
});

export const Legenda = memo(function Legenda() {
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isAuth = oraPrenotaStore((state) => state.isAuthenticated());

  const { isLoading, data } = useQuery<unknown, AxiosError, { definizioni: TariffeData[] }>(
    ["tariffe-definizioni/findAll"],
    () => axios.get(API_URL + "tariffe-definizioni/findMy").then((res) => res.data),
    { enabled: open && isAuth }
  );

  return (
    <>
      <Button startIcon={<InfoIcon />} onClick={() => setOpen(true)}>
        Legenda
      </Button>
      <Dialog open={open} onClose={handleClose} fullScreen={fullScreen}>
        <DialogTitle>Legenda quadro</DialogTitle>
        <DialogContent sx={{ minWidth: fullScreen ? "200px" : "450px" }}>
          <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={1}>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
              <span className="prenotato legendaCasella"></span>
              <span>Prenotato</span>
            </Stack>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
              <span className="prenotabile legendaCasella"></span>
              <span>Disponibile</span>
            </Stack>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
              <span className="tdTariffa legendaCasella"></span>
              <span>Non disponibile</span>
            </Stack>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
              <span className="prenotatoDaMe legendaCasella"></span>
              <span>Prenotato da te</span>
            </Stack>
          </Stack>

          {isAuth && (
            <>
              <DialogTitle paddingLeft={0}>Legenda tariffe</DialogTitle>
              {isLoading && <Loading />}
              <Table size="small">
                <TableBody>
                  {data?.definizioni.map((t) => (
                    <TariffaRow t={t} key={t.id} />
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
